import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCartStore } from "../store/cartStore";
import { getPrefaceFrame, getPrefaceContent } from "../lib/api";
import PrefaceTrackingCard from "../components/PrefaceTrackingCard";

/**
 * Préface La BASE — WEB premium JOUR/NUIT (parité avec l'app mobile Expo).
 * Fonds officiels validés (UI gravée) + zones cliquables transparentes + carte de
 * suivi VORTEX réelle (affichée UNIQUEMENT si livraison active). Aucun visuel régénéré.
 * Affichage en colonne "smartphone" centrée, y compris sur ordinateur.
 */
const BG_DAY_ACTIVE = "/brand/jour_active_ncd.webp";
const BG_NIGHT_ACTIVE = "/brand/nuit_active_ncd.webp";
const BG_WAIT = "/brand/jour_attente_ncd.webp?v=7";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Coordonnées normalisées (identiques au mobile) — cadre gravé mesuré.
const Z_DAY = {
  base: { l: 0.10, t: 0.108, w: 0.27, h: 0.036 },
  track: { l: 0.020, t: 0.279, w: 0.958, h: 0.190 },
  transport: { l: 0.495, t: 0.300, w: 0.475, h: 0.185 },
  search: { l: 0.030, t: 0.502, w: 0.940, h: 0.042 },
  migros: { l: 0.024, t: 0.481, w: 0.464, h: 0.2035 },
  resto: { l: 0.512, t: 0.481, w: 0.464, h: 0.2035 },
  kiosk: { l: 0.024, t: 0.6955, w: 0.464, h: 0.2035 },
  pharma: { l: 0.512, t: 0.6955, w: 0.464, h: 0.2035 },
  navTop: 0.922, navH: 0.066,
};
const Z_NIGHT = {
  base: { l: 0.10, t: 0.100, w: 0.27, h: 0.036 },
  track: { l: 0.022, t: 0.263, w: 0.956, h: 0.247 },
  search: { l: 0.030, t: 0.515, w: 0.940, h: 0.042 },
  migros: { l: 0.022, t: 0.515, w: 0.468, h: 0.197 },
  resto: { l: 0.510, t: 0.515, w: 0.468, h: 0.197 },
  kiosk: { l: 0.022, t: 0.726, w: 0.468, h: 0.197 },
  pharma: { l: 0.510, t: 0.726, w: 0.468, h: 0.197 },
  navTop: 0.935, navH: 0.066,
};

const CUBE_ROUTES = {
  resto: "/restaurant-boissons",
  kiosk: "/univers/kiosk",
  pharma: "/services-domicile",
  migros: "/personal-shopper",
};
// Cube = cadre ; image = contenu REMPLAÇABLE (changez juste le chemin ci-dessous).
const CUBE_IMG = {
  resto: "/cubes/cube-resto.jpg",
  kiosk: "/cubes/cube-kiosk.jpg",
  pharma: "/cubes/cube-soin.jpg",
  migros: "/cubes/cube-mes-courses.jpg?v=6",
};
const COOP_LOGO = "/cubes/coop-logo.png?v=4";
const ALDI_LOGO = "/cubes/aldi-logo.png?v=7";
const CUBE_GLOW = {
  resto: "0 0 0 2px #EAB308",
  kiosk: "0 0 0 2px #2563EB",
  pharma: "0 0 0 2px #16A34A",
  migros: "0 0 0 2px #E11D1D",
};
const CUBE_TINT = {
  resto: "rgba(234,179,8,0.85)",
  kiosk: "rgba(37,99,235,0.85)",
  pharma: "rgba(22,163,74,0.85)",
  migros: "rgba(225,29,29,0.85)",
};
const CUBE_META = {
  resto: { color: "#EAB308", name: "Restaurants et boissons" },
  kiosk: { color: "#2563EB", name: "Kiosk et bazar" },
  pharma: { color: "#16A34A", name: "Soins et services" },
  migros: { color: "#E11D1D", name: "Mes courses" },
};
const CUBE_ICON = {
  migros: (<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="21" r="1.4" /><circle cx="18" cy="21" r="1.4" /><path d="M2 3h3l2.3 11.3a2 2 0 0 0 2 1.6h8a2 2 0 0 0 2-1.5L22 7H6" /></svg>),
  resto: (<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 13.9A4 4 0 0 1 7.4 6 5 5 0 0 1 16.6 6 4 4 0 0 1 18 13.9V21H6Z" /><line x1="7" y1="17.5" x2="17" y2="17.5" /></svg>),
  kiosk: (<span className="pf-k">K</span>),
  pharma: (<svg viewBox="0 0 24 24" fill="#fff" stroke="none"><path d="M12 20.8l-1.45-1.32C5.4 14.86 2 11.78 2 8.0 2 5.0 4.42 2.6 7.5 2.6c1.74 0 3.41.8 4.5 2.08C13.09 3.4 14.76 2.6 16.5 2.6 19.58 2.6 22 5.0 22 8.0c0 3.78-3.4 6.86-8.55 11.49L12 20.8z"/></svg>),
};
const NAV = [
  { key: "home", to: "/" },
  { key: "tyluss", to: "/tyluss" },
  { key: "cart", to: "/panier" },
  { key: "orders", to: "/commandes" },
  { key: "profile", to: "/profil" },
];

function isNightNow() {
  try {
    const h = Number(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Zurich", hour: "2-digit", hour12: false }).format(new Date()));
    return h >= 19 || h < 7;
  } catch {
    const h = new Date().getHours();
    return h >= 19 || h < 7;
  }
}

function readActiveDelivery() {
  try {
    const raw = localStorage.getItem("lb_active_delivery");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const CSS = `
.pf-root{min-height:100vh;width:100%;display:flex;justify-content:center;align-items:flex-start;background:#06130b;overflow:hidden;}
.pf-stage{position:relative;width:100%;max-width:480px;line-height:0;background:#06130b;}
.pf-stage>img.pf-bg{width:100%;height:auto;display:block;user-select:none;-webkit-user-drag:none;}
.pf-zone{position:absolute;background:transparent;border:0;padding:0;cursor:pointer;z-index:6;-webkit-tap-highlight-color:transparent;}
.pf-zone:focus-visible{outline:none;}
.pf-cube{overflow:hidden;border-radius:26px;transition:background .13s ease, box-shadow .13s ease;}
.pf-cube:active{background:radial-gradient(120% 120% at 50% 50%, rgba(255,255,255,.18), rgba(255,255,255,.05) 55%, rgba(255,255,255,0) 76%);
  box-shadow:inset 0 0 0 2px rgba(255,255,255,.26), inset 0 0 24px rgba(255,255,255,.16);}
.pf-cube2{overflow:hidden;border-radius:18px;background:#06130b;padding:0;line-height:0;transition:transform .12s ease;}
.pf-cube2>img{width:100%;height:100%;object-fit:cover;object-position:50% 50%;display:block;-webkit-user-drag:none;user-select:none;}
.pf-cube2:active{transform:scale(.975);}
.pf-badge-c{position:absolute;left:50%;top:40%;transform:translate(-50%,-50%);width:56px;height:56px;border-radius:14px;
  display:flex;align-items:center;justify-content:center;z-index:9;pointer-events:none;}
.pf-badge-c svg{width:31px;height:31px;}
.pf-k{color:#E11D1D;font:900 35px/1 system-ui,sans-serif;}
.pf-cubes-tray{position:absolute;z-index:2;background:#06241A;border-radius:16px;}
.pf-chip{position:absolute;top:40%;transform:translate(-50%,-50%);width:30%;height:20%;z-index:6;pointer-events:none;
  background:#fff;border-radius:9px;padding:3px 6px;box-shadow:0 3px 10px rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;}
.pf-chip.coop{left:18.8%;}
.pf-chip.aldi{left:81.2%;}
.pf-chip > img{max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;display:block;}
.pf-band{position:absolute;left:0;right:0;bottom:0;height:28%;display:flex;align-items:center;justify-content:center;
  z-index:8;pointer-events:none;color:#fff;font-family:'Anton',system-ui,sans-serif;font-weight:400;font-size:21px;line-height:.92;
  letter-spacing:.6px;text-transform:uppercase;text-shadow:0 2px 8px rgba(0,0,0,.55);padding:0 8px;text-align:center;}
.pf-track{position:absolute;z-index:7;}
.pf-voice{position:absolute;z-index:7;display:flex;align-items:center;justify-content:flex-end;padding:0 8px 0 0;cursor:pointer;background:transparent;
  border-radius:18px 18px 18px 5px;box-shadow:0 0 0 1.6px rgba(255,150,40,.82), 0 0 18px rgba(255,150,40,.42), inset 0 0 14px rgba(255,150,40,.10);}
.pf-voice .pf-voice-mic{width:30px;height:30px;border-radius:999px;display:flex;align-items:center;justify-content:center;
  background:rgba(6,19,11,.55);border:1px solid rgba(255,150,40,.78);box-shadow:0 2px 8px rgba(0,0,0,.45);}
.pf-badge{position:absolute;min-width:16px;height:16px;padding:0 4px;border-radius:9px;background:#e42313;color:#fff;
  font:800 10px/16px system-ui,sans-serif;text-align:center;top:14%;left:62%;z-index:8;pointer-events:none;}
`;

export default function Preface() {
  const nav = useNavigate();
  const cartCount = useCartStore((s) => s.getItemCount());
  const [track, setTrack] = useState(null);
  const [orderRef, setOrderRef] = useState("");
  const [content, setContent] = useState({
    bubbleText: "Hello, comment puis-je vous aider ?",
    cubeMigros: "Mes courses",
    cubeResto: "Restaurants et boissons",
    cubeKiosk: "Kiosk et bazar",
    cubePharma: "Soins et services",
  });
  const mounted = useRef(true);

  const cubeName = (k) => content[{ migros: "cubeMigros", resto: "cubeResto", kiosk: "cubeKiosk", pharma: "cubePharma" }[k]] || CUBE_META[k].name;

  const load = useCallback(async () => {
    try {
      const order = readActiveDelivery();
      const token = order?.track_token || null;
      // TULUSS pilote le cadre (cascade commande VORTEX > … > attente).
      const frame = await getPrefaceFrame(token);
      if (!mounted.current) return;
      if (frame.state === "active") {
        setTrack(frame.track);
        const num = order?.order_number || frame.track?.order_id || "";
        setOrderRef("#" + String(num).replace(/[^a-zA-Z0-9]/g, "").slice(0, 6).toUpperCase());
      } else {
        setTrack(null); setOrderRef("");
      }
    } catch {
      if (mounted.current) { setTrack(null); setOrderRef(""); }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    getPrefaceContent().then((c) => { if (mounted.current && c) setContent((p) => ({ ...p, ...c })); }).catch(() => {});
    const id = setInterval(load, 20000);
    return () => { mounted.current = false; clearInterval(id); };
  }, [load]);

  // pfmode : QA visuel du rendu JOUR/NUIT uniquement (sans paramètre = bascule horaire auto).
  const qp = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const forceMode = qp.get("pfmode");
  const night = forceMode ? forceMode === "night" : isNightNow();
  const active = !!track;

  // Attente = UNE seule image (identique jour/nuit) → layout JOUR. Actif = jour/nuit.
  const Z = active && night ? Z_NIGHT : Z_DAY;
  const BG = active
    ? (night ? BG_NIGHT_ACTIVE : BG_DAY_ACTIVE)
    : BG_WAIT;
  const cardMode = active && night ? "night" : "day";

  const pct = (z) => ({ left: `${z.l * 100}%`, top: `${z.t * 100}%`, width: `${z.w * 100}%`, height: `${z.h * 100}%` });

  return (
    <div className="pf-root">
      <style>{CSS}</style>
      <div className="pf-stage">
        <img className="pf-bg" src={BG} alt="Accueil La BASE" data-testid="preface-bg" />

        <button className="pf-zone" data-testid="preface-base" aria-label="Changer de base" style={pct(Z.base)} onClick={() => nav("/profil")} />

        {active && (
          <div className="pf-track" data-testid="preface-track-wrap" style={pct(Z.track)}>
            <PrefaceTrackingCard
              track={track}
              orderRef={orderRef}
              backend={BACKEND}
              mode={cardMode}
              onClick={() => nav("/commandes")}
            />
          </div>
        )}

        <button className="pf-zone pf-voice" data-testid="preface-search" aria-label="Recherche TYLUSS"
          style={{ left: "37.5%", top: "16.8%", width: "30%", height: "8.8%" }} onClick={() => nav("/tyluss")} />

        <div className="pf-cubes-tray" style={{ left: "0%", top: "47%", width: "100%", height: "44%" }} aria-hidden="true" />

        {["resto", "kiosk", "pharma", "migros"].map((k) => (
          <button key={k} className="pf-zone pf-cube2" data-testid={`preface-cube-${k}`} aria-label={k}
            style={{ ...pct(Z[k]), boxShadow: CUBE_GLOW[k] }} onClick={() => nav(CUBE_ROUTES[k])}>
            <img src={CUBE_IMG[k]} alt={k} draggable="false" />
            {k === "migros" && (
              <>
                <span className="pf-chip coop" aria-hidden="true"><img src={COOP_LOGO} alt="Coop" draggable="false" /></span>
                <span className="pf-chip aldi" aria-hidden="true"><img src={ALDI_LOGO} alt="Aldi" draggable="false" /></span>
              </>
            )}
            <span className="pf-badge-c" style={{ background: CUBE_TINT[k], boxShadow: `inset 0 0 0 2px ${CUBE_META[k].color}, 0 4px 12px rgba(0,0,0,.4)`, backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)" }} aria-hidden="true">{CUBE_ICON[k]}</span>
            <span className="pf-band" style={{ background: CUBE_META[k].color }}>{cubeName(k)}</span>
          </button>
        ))}

        {NAV.map((n, i) => (
          <button key={n.key} className="pf-zone" data-testid={`preface-nav-${n.key}`} aria-label={n.key}
            style={{ left: `${i * 20}%`, top: `${Z.navTop * 100}%`, width: "20%", height: `${Z.navH * 100}%` }}
            onClick={() => nav(n.to)}>
            {n.key === "cart" && cartCount > 0 && (
              <span className="pf-badge" data-testid="preface-cart-badge">{cartCount > 99 ? "99+" : cartCount}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
